import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import { cn, getProjectStatusColor, getProjectStatusLabel, formatRelativeTime } from '@/lib/utils'
import {
    Plus,
    FolderOpen,
    Film,
    FileText,
    LayoutGrid,
    List,
    Search,
    Loader2,
} from 'lucide-react'

interface Project {
    id: string
    name: string
    description: string | null
    type: string
    status: string
    coverImageUrl: string | null
    createdAt: number
    updatedAt: number
    scriptsCount: number
    beatBoardsCount: number
}

export default function DashboardPage() {
    const queryClient = useQueryClient()
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
    const [searchQuery, setSearchQuery] = useState('')
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
    const [newProjectName, setNewProjectName] = useState('')
    const [newProjectDescription, setNewProjectDescription] = useState('')
    const [newProjectType, setNewProjectType] = useState('feature')

    const { data, isLoading, error } = useQuery({
        queryKey: ['projects'],
        queryFn: () => api.get<Project[]>('/projects'),
    })

    const createMutation = useMutation({
        mutationFn: (data: { name: string; description?: string; type?: string }) =>
            api.post('/projects', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] })
            setIsCreateDialogOpen(false)
            setNewProjectName('')
            setNewProjectDescription('')
            setNewProjectType('feature')
            toast({ title: 'Project created', description: 'Your new project is ready.' })
        },
        onError: (error: any) => {
            toast({
                title: 'Failed to create project',
                description: error.message,
                variant: 'destructive',
            })
        },
    })

    const projects = data?.data || []
    const filteredProjects = projects.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
    )

    const handleCreateProject = (e: React.FormEvent) => {
        e.preventDefault()
        createMutation.mutate({
            name: newProjectName,
            description: newProjectDescription || undefined,
            type: newProjectType,
        })
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <p className="text-destructive mb-4">Failed to load projects</p>
                <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['projects'] })}>
                    Retry
                </Button>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Projects</h1>
                    <p className="text-muted-foreground">
                        Manage your animation projects
                    </p>
                </div>

                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                    <DialogTrigger asChild>
                        <Button>
                            <Plus className="mr-2 h-4 w-4" />
                            New Project
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <form onSubmit={handleCreateProject}>
                            <DialogHeader>
                                <DialogTitle>Create New Project</DialogTitle>
                                <DialogDescription>
                                    Start a new animation project. You can add team members and content later.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="projectName">Project Name</Label>
                                    <Input
                                        id="projectName"
                                        placeholder="My Awesome Animation"
                                        value={newProjectName}
                                        onChange={(e) => setNewProjectName(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="projectType">Type</Label>
                                    <select
                                        id="projectType"
                                        value={newProjectType}
                                        onChange={(e) => setNewProjectType(e.target.value)}
                                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                    >
                                        <option value="feature">Feature Film</option>
                                        <option value="series">Series</option>
                                        <option value="short">Short Film</option>
                                        <option value="commercial">Commercial</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="projectDescription">Description (optional)</Label>
                                    <Input
                                        id="projectDescription"
                                        placeholder="A short description..."
                                        value={newProjectDescription}
                                        onChange={(e) => setNewProjectDescription(e.target.value)}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsCreateDialogOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={createMutation.isPending}>
                                    {createMutation.isPending && (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    )}
                                    Create Project
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search projects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <div className="flex items-center border rounded-lg">
                    <button
                        onClick={() => setViewMode('grid')}
                        className={cn(
                            "p-2 transition-colors",
                            viewMode === 'grid' ? 'bg-accent' : 'hover:bg-accent/50'
                        )}
                    >
                        <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        className={cn(
                            "p-2 transition-colors",
                            viewMode === 'list' ? 'bg-accent' : 'hover:bg-accent/50'
                        )}
                    >
                        <List className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Empty state */}
            {filteredProjects.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed rounded-lg">
                    <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No projects yet</h3>
                    <p className="text-muted-foreground mb-4">
                        Get started by creating your first project
                    </p>
                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Create Project
                    </Button>
                </div>
            )}

            {/* Projects grid */}
            {viewMode === 'grid' && filteredProjects.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProjects.map((project) => (
                        <Link
                            key={project.id}
                            to={`/projects/${project.id}`}
                            className="group block border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200 hover:border-primary/50"
                        >
                            {/* Cover image */}
                            <div className="h-32 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                                {project.coverImageUrl ? (
                                    <img
                                        src={project.coverImageUrl}
                                        alt={project.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <Film className="h-12 w-12 text-primary/40" />
                                )}
                            </div>

                            {/* Content */}
                            <div className="p-4">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <h3 className="font-semibold group-hover:text-primary transition-colors">
                                        {project.name}
                                    </h3>
                                    <div className={cn(
                                        "w-2 h-2 rounded-full shrink-0 mt-2",
                                        getProjectStatusColor(project.status)
                                    )} />
                                </div>

                                {project.description && (
                                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                        {project.description}
                                    </p>
                                )}

                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                        <FileText className="h-3.5 w-3.5" />
                                        {project.scriptsCount} scripts
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <LayoutGrid className="h-3.5 w-3.5" />
                                        {project.beatBoardsCount} boards
                                    </div>
                                </div>

                                <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-muted-foreground">
                                    <span className="capitalize">{getProjectStatusLabel(project.status)}</span>
                                    <span>Updated {formatRelativeTime(project.updatedAt)}</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {/* Projects list */}
            {viewMode === 'list' && filteredProjects.length > 0 && (
                <div className="border rounded-lg divide-y">
                    {filteredProjects.map((project) => (
                        <Link
                            key={project.id}
                            to={`/projects/${project.id}`}
                            className="flex items-center gap-4 p-4 hover:bg-accent/50 transition-colors"
                        >
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shrink-0">
                                <Film className="h-6 w-6 text-primary/60" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <h3 className="font-medium truncate">{project.name}</h3>
                                    <span className={cn(
                                        "px-2 py-0.5 rounded-full text-xs",
                                        getProjectStatusColor(project.status),
                                        "text-white"
                                    )}>
                                        {getProjectStatusLabel(project.status)}
                                    </span>
                                </div>
                                {project.description && (
                                    <p className="text-sm text-muted-foreground truncate">
                                        {project.description}
                                    </p>
                                )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {formatRelativeTime(project.updatedAt)}
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    )
}
